import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface ScrambleTextProps {
    text: string
    epoch?: number
    duration?: number
    className?: string
}

const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/+-:'

export const scramble = (text: string, progress: number): string => {
    const reveal = Math.floor(progress * text.length)

    let out = ''

    for (let i = 0; i < text.length; i++) {
        const char = text[i]

        if (i < reveal || char === ' ' || char === '/' || char === '.' || char === '·') out += char
        else out += GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
    }

    return out
}

/**
 * HUD text that runs through random mono characters before resolving to its new value.
 */
export const ScrambleText: FC<ScrambleTextProps> = ({ text, epoch = 0, duration = 420, className }): ReactNode => {
    const ref = useRef<HTMLSpanElement>(null)
    const mounted = useRef(false)
    const reduced = useReducedMotion()
    const [initial] = useState(text)

    useEffect(() => {
        const element = ref.current

        if (!element) return

        if (!mounted.current || reduced) {
            mounted.current = true
            element.textContent = text

            return
        }

        let raf = 0
        const start = performance.now()

        const frame = (now: number): void => {
            const progress = Math.min(1, (now - start) / duration)

            element.textContent = progress < 1 ? scramble(text, progress) : text

            if (progress < 1) raf = requestAnimationFrame(frame)
        }

        raf = requestAnimationFrame(frame)

        return () => {
            cancelAnimationFrame(raf)
            element.textContent = text
        }
    }, [text, epoch, duration, reduced])

    return (
        <>
            <span ref={ref} className={className} aria-hidden='true'>
                {initial}
            </span>

            <span className='sr-only'>
                {text}
            </span>
        </>
    )
}
